import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './IngredientDetail.css';
import '../pages/Landing.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConsumerLabel =
  | 'raises concerns'
  | 'limit intake'
  | 'research ongoing'
  | 'no nutritional value'
  | 'generally ok';

interface FaqItem { question: string; answer: string; }
interface SourceItem { title: string; authors?: string; year?: number; url?: string; }

interface Ingredient {
  slug: string;
  name: string;
  aliases: string[];
  category: string;
  health_classification: string;
  evidence_strength: string;
  nova_score: number | null;
  foodscan_summary: string;
  consumer_label: ConsumerLabel;
  foodscan_flags: string[];
  short_description: string;
  what_is_it: string;
  how_its_made: string | null;
  common_sources: string[];
  health_considerations: string;
  scientific_context: string | null;
  regulatory_status_us: string | null;
  regulatory_status_intl: string | null;
  mosaime_tip: string | null;
  seo_title: string | null;
  meta_description: string | null;
  faq: FaqItem[];
  sources: SourceItem[];
  related_ingredient_slugs: string[];
}

// ---------------------------------------------------------------------------
// Display config
// ---------------------------------------------------------------------------

const LABEL_BADGE: Record<ConsumerLabel, { bg: string; text: string }> = {
  'raises concerns':      { bg: '#fce4ec', text: '#c62828' },
  'limit intake':         { bg: '#fff3e0', text: '#e65100' },
  'research ongoing':     { bg: '#e3f2fd', text: '#1565c0' },
  'no nutritional value': { bg: '#eceff1', text: '#546e7a' },
  'generally ok':         { bg: '#e8f5e9', text: '#2e7d32' },
};

const NOVA_SUBLABEL: Record<number, string> = {
  1: 'Unprocessed',
  2: 'Minimally processed',
  3: 'Processed',
  4: 'Ultra-processed',
};

function titleFromSlug(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Imperatively manage the document <title>, meta description, and FAQ JSON-LD.
function useSeo(ing: Ingredient | null) {
  useEffect(() => {
    if (!ing) return;

    const prevTitle = document.title;
    document.title = ing.seo_title || `${ing.name} | mosaime`;

    let metaEl = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const createdMeta = !metaEl;
    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', 'description');
      document.head.appendChild(metaEl);
    }
    const prevDesc = metaEl.getAttribute('content');
    metaEl.setAttribute('content', ing.meta_description || ing.short_description || '');

    let ld: HTMLScriptElement | null = null;
    if (Array.isArray(ing.faq) && ing.faq.length > 0) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: ing.faq.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      });
      document.head.appendChild(ld);
    }

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        metaEl?.remove();
      } else if (metaEl) {
        if (prevDesc === null) metaEl.removeAttribute('content');
        else metaEl.setAttribute('content', prevDesc);
      }
      ld?.remove();
    };
  }, [ing]);
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="ing-page">
      <PublicHeader />
      {children}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ accordion item
// ---------------------------------------------------------------------------

function FaqRow({ item }: { item: FaqItem }) {
  return (
    <details className="ing-faq-item">
      <summary>{item.question}</summary>
      <p>{item.answer}</p>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function IngredientDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [ing, setIng] = useState<Ingredient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'error' | null>(null);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!slug) { setError('not_found'); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setIng(null);

    (async () => {
      try {
        const res = await fetch(`/api/ingredients/${slug}`);
        if (cancelled) return;
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (!res.ok) { setError('error'); setLoading(false); return; }
        const json = await res.json();
        setIng(json.data);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  useSeo(ing);

  if (loading) {
    return (
      <Shell>
        <div className="ing-loading">
          <div className="ing-spinner" />
          <p>Loading…</p>
        </div>
      </Shell>
    );
  }

  if (error || !ing) {
    return (
      <Shell>
        <div className="ing-not-found">
          <div className="ing-not-found-icon">🔍</div>
          <h2>{error === 'not_found' ? 'Ingredient not found' : 'Something went wrong'}</h2>
          <p>
            {error === 'not_found'
              ? "We don't have a page for this ingredient yet."
              : "We couldn't load this ingredient. Please try again."}
          </p>
          <button className="btn hero-cta" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </Shell>
    );
  }

  const badge = LABEL_BADGE[ing.consumer_label];

  return (
    <Shell>
      {/* ── Header ── */}
      <header className="ing-hero">
        <div className="landing-container ing-hero-inner">
          <Link to="/" className="ing-back">&larr; mosaime ingredient guide</Link>
          <h1 className="ing-title">{ing.name}</h1>
          <div className="ing-hero-badges">
            <span className="ing-label-badge" style={{ background: badge.bg, color: badge.text }}>
              {ing.consumer_label}
            </span>
            {ing.nova_score && (
              <span className="ing-nova">
                NOVA {ing.nova_score}
                <span className="ing-nova-sub">{NOVA_SUBLABEL[ing.nova_score]}</span>
              </span>
            )}
            {ing.category && <span className="ing-category">{ing.category}</span>}
          </div>
          {ing.short_description && <p className="ing-lede">{ing.short_description}</p>}
          {ing.aliases?.length > 0 && (
            <p className="ing-aliases">Also known as: {ing.aliases.join(', ')}</p>
          )}
        </div>
      </header>

      <div className="landing-container ing-body">

        {ing.what_is_it && (
          <section className="ing-section">
            <h2 className="ing-section-title">What is it?</h2>
            <p>{ing.what_is_it}</p>
          </section>
        )}

        {ing.how_its_made && (
          <section className="ing-section">
            <h2 className="ing-section-title">How it's made</h2>
            <p>{ing.how_its_made}</p>
          </section>
        )}

        {ing.common_sources?.length > 0 && (
          <section className="ing-section">
            <h2 className="ing-section-title">Where you'll find it</h2>
            <div className="ing-chip-list">
              {ing.common_sources.map((s) => (
                <span key={s} className="ing-chip">{s}</span>
              ))}
            </div>
          </section>
        )}

        {ing.health_considerations && (
          <section className="ing-section">
            <h2 className="ing-section-title">Health considerations</h2>
            <p>{ing.health_considerations}</p>
          </section>
        )}

        {ing.scientific_context && (
          <section className="ing-section">
            <h2 className="ing-section-title">What the research says</h2>
            <p>{ing.scientific_context}</p>
          </section>
        )}

        {(ing.regulatory_status_us || ing.regulatory_status_intl) && (
          <section className="ing-section">
            <h2 className="ing-section-title">Regulatory status</h2>
            {ing.regulatory_status_us && (
              <p><strong>United States:</strong> {ing.regulatory_status_us}</p>
            )}
            {ing.regulatory_status_intl && (
              <p><strong>International:</strong> {ing.regulatory_status_intl}</p>
            )}
          </section>
        )}

        {ing.mosaime_tip && (
          <section className="ing-tip">
            <h2 className="ing-tip-title">mosaime tip</h2>
            <p>{ing.mosaime_tip}</p>
          </section>
        )}

        {ing.faq?.length > 0 && (
          <section className="ing-section">
            <h2 className="ing-section-title">Frequently asked questions</h2>
            <div className="ing-faq">
              {ing.faq.map((f, i) => <FaqRow key={i} item={f} />)}
            </div>
          </section>
        )}

        {ing.related_ingredient_slugs?.length > 0 && (
          <section className="ing-section">
            <h2 className="ing-section-title">Related ingredients</h2>
            <div className="ing-chip-list">
              {ing.related_ingredient_slugs.map((s) => (
                <Link key={s} to={`/ingredients/${s}`} className="ing-chip ing-chip--link">
                  {titleFromSlug(s)}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="ing-disclaimer">
          Educational information only — not medical advice. Always verify with product labels and a healthcare professional.
        </div>

        {/* ── CTA ── */}
        <div className="ing-cta">
          <h3>Know what's really in your food</h3>
          <p>mosaime helps you scan ingredients and make better choices — straight from your phone via SMS.</p>
          <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
        </div>

      </div>
    </Shell>
  );
}

export default IngredientDetailPage;
